import { Button } from "@/components/ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/cn";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PeriodNavigatorProps<T extends string> {
  periods: T[];
  selected: T;
  current?: T;
  onChange: (value: T) => void;
  formatOption: (value: T) => string;
  selectAriaLabel: string;
  className?: string;
}

export function PeriodNavigator<T extends string>({
  periods,
  selected,
  current,
  onChange,
  formatOption,
  selectAriaLabel,
  className,
}: PeriodNavigatorProps<T>) {
  const selectedIndex = periods.indexOf(selected);

  return (
    <div className={cn("flex items-center justify-center gap-2", className)}>
      <Button
        variant="secondary"
        size="sm"
        className="h-9 w-9 shrink-0 rounded-lg p-0"
        disabled={selectedIndex <= 0}
        onClick={() => onChange(periods[selectedIndex - 1]!)}
        aria-label="Previous period"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <Select value={selected} onValueChange={(value) => onChange(value as T)}>
        <SelectTrigger className="min-w-48" aria-label={selectAriaLabel}>
          <SelectValue>
            {formatOption(selected)}
            {current && selected === current ? " (current)" : ""}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {periods.map((period) => (
            <SelectItem key={period} value={period}>
              {formatOption(period)}
              {current && period === current ? " (current)" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant="secondary"
        size="sm"
        className="h-9 w-9 shrink-0 rounded-lg p-0"
        disabled={selectedIndex >= periods.length - 1}
        onClick={() => onChange(periods[selectedIndex + 1]!)}
        aria-label="Next period"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
